import React, { useState } from 'react';
import { signIn, confirmSignIn, fetchAuthSession } from '@aws-amplify/auth';

const Login = ({ onLogin }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [error, setError] = useState('');
    const [requiresNewPassword, setRequiresNewPassword] = useState(false);
    const [signInSession, setSignInSession] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const validatePassword = (password) => {
        const minLength = 8;
        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);
        const hasNumbers = /\d/.test(password);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

        if (password.length < minLength) {
            return `Password must be at least ${minLength} characters long`;
        }
        if (!hasUpperCase) {
            return 'Password must contain at least one uppercase letter';
        }
        if (!hasLowerCase) {
            return 'Password must contain at least one lowercase letter';
        }
        if (!hasNumbers) {
            return 'Password must contain at least one number';
        }
        if (!hasSpecialChar) {
            return 'Password must contain at least one special character';
        }
        return null;
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            console.log('Attempting sign in with:', { 
                username: `"${username}"`, 
                passwordLength: password.length,
                timestamp: new Date().toISOString()
            });
            
            const signInResponse = await signIn({ username, password });
            console.log('SignIn Response:', JSON.stringify(signInResponse, null, 2));

            if (signInResponse.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
                setRequiresNewPassword(true);
                setSignInSession(signInResponse);
            } else if (signInResponse.isSignedIn) {
                // Fetch the session to get tokens
                try {
                    const session = await fetchAuthSession();
                    const idToken = session.tokens?.idToken?.toString();
                    console.log('✅ Login successful, token retrieved');
                    onLogin({
                        username,
                        token: idToken
                    });
                } catch (sessionErr) {
                    console.warn('Could not fetch session after login, proceeding anyway:', sessionErr);
                    onLogin({ username });
                }
            } else {
                setError('Unexpected sign-in response. Please try again.');
            }
        } catch (err) {
            console.error('Login error:', err);
            
            // Provide user-friendly error messages
            if (err.name === 'NotAuthorizedException') {
                setError('Incorrect username or password.');
            } else if (err.name === 'UserNotFoundException') {
                setError('User does not exist.');
            } else if (err.name === 'UserNotConfirmedException') {
                setError('User account is not confirmed. Please check your email.');
            } else if (err.name === 'NetworkError' || err.message?.includes('Network')) {
                setError('Network error. Please check your connection and try again.');
            } else {
                setError(err.message || 'An error occurred during sign in');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleNewPasswordSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        if (newPassword !== confirmNewPassword) {
            setError('New passwords do not match');
            setIsLoading(false);
            return;
        }

        const passwordError = validatePassword(newPassword);
        if (passwordError) {
            setError(passwordError);
            setIsLoading(false);
            return;
        }

        try {
            if (!signInSession) {
                throw new Error('No sign-in session available');
            }
            
            const response = await confirmSignIn({
                challengeResponse: newPassword,
            });
            
            console.log('New password confirmation response:', response);
            
            if (response.isSignedIn) {
                setRequiresNewPassword(false);
                setNewPassword('');
                setConfirmNewPassword('');
                
                // Fetch session after password change
                try {
                    const session = await fetchAuthSession();
                    const idToken = session.tokens?.idToken?.toString();
                    onLogin({
                        username,
                        token: idToken
                    });
                } catch (sessionErr) {
                    console.warn('Could not fetch session after password change:', sessionErr);
                    onLogin({ username });
                }
            } else {
                setError('Password change was not successful. Please try again.');
            }
        } catch (err) {
            setError(err.message || 'An error occurred while setting the new password');
            console.error('New password submission error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackToLogin = () => {
        setRequiresNewPassword(false);
        setNewPassword('');
        setConfirmNewPassword('');
        setError('');
        setSignInSession(null);
    };

    return (
        <div style={styles.container}>
            <div style={styles.loginBox}>
                <div style={styles.logoContainer}>
                    <div style={styles.logo}>
                        <svg style={styles.logoIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                    </div>
                    <h1 style={styles.brandTitle}>DataWeb AI</h1>
                </div>

                <h2 style={styles.title}>
                    {requiresNewPassword ? 'Set New Password' : 'Sign In'}
                </h2>
                {error && <p style={styles.error}>{error}</p>}
                
                {!requiresNewPassword ? (
                    <>
                        <form onSubmit={handleLogin}>
                            <input
                                type="text"
                                placeholder="Username"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                style={styles.input}
                                autoComplete="username"
                                required
                                disabled={isLoading}
                            />
                            <input
                                type="password"
                                placeholder="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                style={styles.input}
                                autoComplete="current-password"
                                required
                                disabled={isLoading}
                            />
                            <button 
                                type="submit" 
                                style={{
                                    ...styles.button,
                                    backgroundColor: isLoading ? '#ccc' : '#007bff',
                                    cursor: isLoading ? 'not-allowed' : 'pointer'
                                }}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Signing In...' : 'Sign In'}
                            </button>
                        </form>
                        
                        <div style={styles.betaNotice}>
                            <p style={styles.betaText}>
                                <strong>Interested in trying DataWeb AI?</strong>
                            </p>
                            <p style={styles.betaText}>
                                Contact <a href="mailto:info@dataweb.ai" style={styles.emailLink}>info@dataweb.ai</a> for a beta account.
                            </p>
                        </div>
                    </>
                ) : (
                    <form onSubmit={handleNewPasswordSubmit}>
                        <p style={styles.info}>
                            You must set a new password before continuing. Please choose a secure password.
                        </p>
                        <div style={styles.passwordRequirements}>
                            <p>Password must contain:</p>
                            <ul>
                                <li>At least 8 characters</li>
                                <li>At least one uppercase letter</li>
                                <li>At least one lowercase letter</li>
                                <li>At least one number</li>
                                <li>At least one special character</li>
                            </ul>
                        </div>
                        <input
                            type="password"
                            placeholder="New Password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            style={styles.input}
                            autoComplete="new-password"
                            required
                            disabled={isLoading}
                        />
                        <input
                            type="password"
                            placeholder="Confirm New Password"
                            value={confirmNewPassword}
                            onChange={(e) => setConfirmNewPassword(e.target.value)}
                            style={styles.input}
                            autoComplete="new-password"
                            required
                            disabled={isLoading}
                        />
                        <button 
                            type="submit" 
                            style={{
                                ...styles.button,
                                backgroundColor: isLoading ? '#ccc' : '#007bff',
                                cursor: isLoading ? 'not-allowed' : 'pointer'
                            }}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Setting Password...' : 'Set New Password'}
                        </button>
                        <button 
                            type="button" 
                            onClick={handleBackToLogin}
                            style={{
                                ...styles.button,
                                backgroundColor: '#6c757d',
                                marginTop: '0.5rem'
                            }}
                            disabled={isLoading}
                        >
                            Back to Login
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

const styles = {
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: '#ffffff',
        padding: '1rem',
    },
    loginBox: {
        backgroundColor: 'white',
        padding: '2.5rem',
        borderRadius: '12px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
        width: '100%',
        maxWidth: '420px',
    },
    logoContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: '2rem',
    },
    logo: {
        width: '64px',
        height: '64px',
        background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)',
        borderRadius: '16px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: '1rem',
        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
    },
    logoIcon: {
        width: '32px',
        height: '32px',
        color: 'white',
    },
    brandTitle: {
        fontSize: '1.75rem',
        fontWeight: 'bold',
        color: '#1f2937',
        margin: 0,
    },
    title: {
        textAlign: 'center',
        marginBottom: '1.5rem',
        color: '#333',
        fontSize: '1.5rem',
        fontWeight: '600',
    },
    error: {
        color: '#dc3545',
        textAlign: 'center',
        marginBottom: '1rem',
        padding: '0.75rem',
        backgroundColor: '#f8d7da',
        border: '1px solid #f5c6cb',
        borderRadius: '6px',
        fontSize: '0.9rem',
    },
    info: {
        color: '#0c5460',
        textAlign: 'center',
        marginBottom: '1rem',
        padding: '0.75rem',
        backgroundColor: '#d1ecf1',
        border: '1px solid #bee5eb',
        borderRadius: '6px',
        fontSize: '0.9rem',
    },
    passwordRequirements: {
        fontSize: '0.85rem',
        color: '#6c757d',
        marginBottom: '1rem',
        padding: '0.75rem',
        backgroundColor: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '6px',
    },
    input: {
        width: '100%',
        padding: '0.875rem',
        marginBottom: '1rem',
        border: '1px solid #ddd',
        borderRadius: '6px',
        boxSizing: 'border-box',
        fontSize: '1rem',
        transition: 'border-color 0.2s',
    },
    button: {
        width: '100%',
        padding: '0.875rem',
        backgroundColor: '#007bff',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '1rem',
        fontWeight: '600',
        transition: 'background-color 0.2s',
    },
    betaNotice: {
        marginTop: '2rem',
        padding: '1.25rem',
        backgroundColor: '#f8f9fa',
        border: '1px solid #e9ecef',
        borderRadius: '8px',
        textAlign: 'center',
    },
    betaText: {
        margin: '0.5rem 0',
        color: '#495057',
        fontSize: '0.95rem',
        lineHeight: '1.5',
    },
    emailLink: {
        color: '#007bff',
        textDecoration: 'none',
        fontWeight: '600',
        transition: 'color 0.2s',
    },
};

export default Login;